/**
 * Emotes (ท่าโพส) and emoji (อีโมจิ). An emote is a pose the character takes and keeps until they
 * move, fight or are struck: a still one holds a single pose, a moving one plays a loop. An emoji
 * pops up over the head for a few seconds. How each looks is the client's (render/emotes.ts).
 */
export type EmoteKind = 'still' | 'loop';

export interface EmoteDef {
  readonly id: string;
  readonly name: string;
  readonly kind: EmoteKind;
  /** Keeps the weapon in hand (a sword dance); otherwise it goes back in its scabbard. */
  readonly armed?: boolean;
  /** A dance (ท่าเต้น): a looping emote listed under its own tab. */
  readonly dance?: boolean;
  /** Seconds one round of the loop takes (dances; the other emotes' timing is the client's). */
  readonly seconds?: number;
}

const list: EmoteDef[] = [
  // ท่านิ่ง
  { id: 'salute', name: 'ประสานหมัด', kind: 'still' },
  { id: 'defeat', name: 'คุกเข่าพ่ายแพ้', kind: 'still' },
  { id: 'akimbo', name: 'เท้าเอว', kind: 'still' },
  { id: 'fold', name: 'กอดอก', kind: 'still' },
  { id: 'think', name: 'ครุ่นคิด', kind: 'still' },
  { id: 'point', name: 'ชี้หน้า', kind: 'still' },
  { id: 'lounge', name: 'นั่งพัก', kind: 'still' },
  { id: 'guard', name: 'ตั้งท่ากระบี่', kind: 'still', armed: true },
  // ท่าเคลื่อนไหว
  { id: 'wave', name: 'โบกมือ', kind: 'loop' },
  { id: 'cheer', name: 'เชียร์', kind: 'loop' },
  { id: 'clap', name: 'ปรบมือ', kind: 'loop' },
  { id: 'laugh', name: 'หัวเราะ', kind: 'loop' },
  { id: 'cry', name: 'ร้องไห้', kind: 'loop' },
  { id: 'dance', name: 'เต้นรำ', kind: 'loop' },
  { id: 'kowtow', name: 'คารวะ', kind: 'loop' },
  { id: 'kata', name: 'รำกระบี่', kind: 'loop', armed: true },
  // งานชาวบ้าน (a tool in hand: the client's characters/props/held.ts)
  { id: 'sweep', name: 'กวาดพื้น', kind: 'loop' },
  { id: 'fish', name: 'ตกปลา', kind: 'loop' },
  // ท่ากายกรรม (the street performers' tumbles)
  { id: 'flip', name: 'ตีลังกากลับหลัง', kind: 'loop' },
  { id: 'handstand', name: 'ยืนด้วยมือ', kind: 'loop' },
  { id: 'kipUp', name: 'ดีดตัวลุก', kind: 'loop' },
  // ท่าเต้น (each one round of `seconds`, drawn by the client's render/dances.ts)
  { id: 'danceFan', name: 'รำพัด', kind: 'loop', dance: true, seconds: 1.6 },
  { id: 'danceSleeve', name: 'รำแขนเสื้อ', kind: 'loop', dance: true, seconds: 2.4 },
  { id: 'danceLion', name: 'ก้าวเชิดสิงโต', kind: 'loop', dance: true, seconds: 1.2 },
  { id: 'danceDrunk', name: 'โยกหมัดเมา', kind: 'loop', dance: true, seconds: 2 },
  { id: 'danceSword', name: 'รำกระบี่มือเปล่า', kind: 'loop', dance: true, seconds: 2.4 },
  { id: 'danceStomp', name: 'ปรบมือกระทืบเท้า', kind: 'loop', dance: true, seconds: 0.9 },
  { id: 'danceTwirl', name: 'หมุนตัว', kind: 'loop', dance: true, seconds: 1.1 },
  { id: 'danceShuffle', name: 'สเต็ปซุกซน', kind: 'loop', dance: true, seconds: 0.8 },
  { id: 'danceJig', name: 'เต้นฉลองชัย', kind: 'loop', dance: true, seconds: 0.7 },
  { id: 'danceTaichi', name: 'รำไทเก๊ก', kind: 'loop', dance: true, seconds: 3.6 },
];

export const EMOTES: Readonly<Record<string, EmoteDef>> = Object.fromEntries(list.map((e) => [e.id, e]));
export const EMOTE_LIST: readonly EmoteDef[] = list;
/** The dances (ท่าเต้น), in menu order. */
export const DANCE_LIST: readonly EmoteDef[] = list.filter((e) => e.dance);

export interface EmojiDef {
  readonly id: string;
  readonly name: string;
}

export const EMOJI_LIST: readonly EmojiDef[] = [
  { id: 'smile', name: 'ยิ้ม' },
  { id: 'laugh', name: 'ขำ' },
  { id: 'love', name: 'รัก' },
  { id: 'cry', name: 'ร้องไห้' },
  { id: 'angry', name: 'โกรธ' },
  { id: 'shock', name: 'ตกใจ' },
  { id: 'sweat', name: 'เหงื่อตก' },
  { id: 'sleep', name: 'ง่วง' },
  { id: 'question', name: 'สงสัย' },
  { id: 'exclaim', name: 'ตื่นเต้น' },
  { id: 'cool', name: 'เท่' },
  { id: 'thumb', name: 'เยี่ยม' },
  { id: 'fist', name: 'สู้!' },
  { id: 'note', name: 'อารมณ์ดี' },
  { id: 'skull', name: 'ตายแล้ว' },
  { id: 'gold', name: 'รวย' },
];
export const EMOJIS: Readonly<Record<string, EmojiDef>> = Object.fromEntries(
  EMOJI_LIST.map((e) => [e.id, e]),
);

/** How long an emoji stays over the head (seconds), and the least time between two. */
export const EMOJI_SECONDS = 3;
export const EMOJI_COOLDOWN = 0.6;
